import { Avatar, Button, Dialog, DialogContent, Grid, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TextField from '../text-field';

const useStyles = makeStyles((theme: any) => ({
  container: { padding: theme.spacing(4, 4, 0) },
  containerFullScreen: { padding: theme.spacing(2, 2, 0) },
  titleContainer: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(5),
  },
  title: {
    color: theme.palette.text.primary,
    textAlign: 'center',
  },
  avatar: { backgroundColor: theme.palette.tab.offselected },
  nameContainer: { paddingLeft: theme.spacing(3) },
  text: { color: theme.palette.tab.offselected },
  descriptionContainer: {
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(5),
  },
  confirmDescriptionContainer: { paddingBottom: theme.spacing(3) },
  confirmContainer: { paddingBottom: theme.spacing(3) },
  // DESIGN.md §5 confirmation-only exception: [Cancel] lower-left, [Delete/Confirm] lower-right.
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(0, 4, 4),
  },
  actionsFullScreen: {
    padding: theme.spacing(2),
    // Keep actions clear of the home indicator when the dialog is full screen.
    paddingBottom: `calc(${theme.spacing(2)} + env(safe-area-inset-bottom))`,
  },
  button: {
    textTransform: 'none',
    '@media (hover: none) and (pointer: coarse)': { minHeight: theme.spacing(5.5) },
  },
  cancelButton: {
    '&&': {
      color: theme.palette.text.primary,
      borderColor: theme.palette.border.main,
    },
  },
  deleteButton: {
    color: theme.palette.error.contrastText,
    backgroundColor: theme.palette.error.main,
    '&:hover': { backgroundColor: theme.palette.error.dark },
    '&&.Mui-disabled': {
      color: theme.palette.error.contrastText,
      backgroundColor: alpha(theme.palette.error.main, 0.5),
    },
  },
  icon: {
    fontSize: 16,
    marginRight: theme.spacing(1),
  },
}));

type Props = {
  open: boolean;
  title: string;
  name: string;
  initials: string;
  description: string;
  confirm?: boolean;
  confirmWord?: string;
  confirmButtonText?: string;
  handleConfirm: () => void;
  handleCancel: () => void;
  // When provided, the dialog stays open and locked until the caller settles the request.
  isLoading?: boolean;
  fullScreen?: boolean;
};

const ConfirmDialog: FC<Props> = ({
  open,
  title,
  name,
  initials,
  description,
  confirm = true,
  confirmWord = 'delete',
  confirmButtonText,
  handleConfirm,
  handleCancel,
  isLoading,
  fullScreen = false,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [value, setValue] = useState('');

  const validateConfirmWord = () => {
    if (!confirm) {
      return false;
    }

    const _value = value.trim().toLowerCase();

    if (_value === '') {
      return true;
    }

    if (_value !== confirmWord!.toLowerCase()) {
      return true;
    }

    return false;
  };

  const controlled = isLoading !== undefined;
  const locked = !!isLoading;

  const onClose = () => {
    if (locked) return;
    setValue('');
    handleCancel();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      disableEscapeKeyDown={locked}
    >
      <DialogContent className={clsx(classes.container, { [classes.containerFullScreen]: fullScreen })}>
        <Grid container direction="column">
          <Grid item xs={12} className={classes.titleContainer}>
            <Typography variant="h6" className={classes.title}>
              {title}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Grid container direction="row" justifyContent="center">
              <Grid item>
                <Grid container direction="row" alignItems="center">
                  <Grid item xs={2}>
                    <Avatar className={classes.avatar}>{initials}</Avatar>
                  </Grid>

                  <Grid item xs={10} className={classes.nameContainer}>
                    <Typography className={classes.text}>
                      {name}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            className={clsx(classes.descriptionContainer, { [classes.confirmDescriptionContainer]: confirm })}
          >
            <Typography className={classes.text}>
              {description}
            </Typography>
          </Grid>

          {confirm && (
            <Grid item xs={12} className={classes.confirmContainer}>
              <TextField
                label={t('users:dialog-confirm-label').replace('${}', confirmWord!.toUpperCase())}
                value={value}
                disabled={locked}
                onChange={(event) => setValue(event.target.value)}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <div className={clsx(classes.actions, { [classes.actionsFullScreen]: fullScreen })}>
        <Button
          variant="outlined"
          disabled={locked}
          className={clsx(classes.button, classes.cancelButton)}
          onClick={onClose}
        >
          <FontAwesomeIcon icon="times" className={classes.icon} />
          {t('common:cancel')}
        </Button>

        <Button
          disableElevation
          variant="contained"
          disabled={locked || validateConfirmWord()}
          className={clsx(classes.button, classes.deleteButton)}
          onClick={() => {
            if (!controlled) onClose();
            handleConfirm();
          }}
        >
          <FontAwesomeIcon
            icon={locked ? 'spinner' : 'trash-alt'}
            spin={locked}
            className={classes.icon}
          />
          {confirmButtonText || t('common:confirm')}
        </Button>
      </div>
    </Dialog>
  );
};

export default ConfirmDialog;